import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Chip,
  Grid,
  Paper,
  Avatar,
  Fab,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ColorLens as ColorLensIcon,
} from '@mui/icons-material';
import { makeApiCall } from '../../global/apiCall';

const colorOptions = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', 
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'
];

const GroupsManager = ({ open, onClose, onGroupSelect, selectedGroup }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, group: null });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    icon: 'folder'
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchGroups();
    }
  }, [open]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await makeApiCall('/groups', 'GET');
      console.log('Full groups API response:', response);
      
      if (response.success && response.data) {
        if (Array.isArray(response.data)) {
          console.log('Groups data is array:', response.data);
          setGroups(response.data);
          setError(''); // Clear any previous error
        } else {
          console.error('Groups response.data is not an array:', response.data);
          setGroups([]);
          setError('Groups data format is incorrect');
        }
      } else {
        console.error('Groups API call failed:', response);
        // Fallback to mock data if API fails
        console.log('Using fallback mock groups data');
        const mockGroups = [
          {
            id: '1',
            name: 'Business',
            description: 'Business contacts',
            color: '#3B82F6',
            isDefault: true,
            cardCount: 0
          },
          {
            id: '2', 
            name: 'Personal',
            description: 'Personal contacts',
            color: '#10B981',
            isDefault: false,
            cardCount: 0
          },
          {
            id: '3',
            name: 'Clients',
            description: 'Client contacts',
            color: '#F59E0B',
            isDefault: false,
            cardCount: 0
          }
        ];
        setGroups(mockGroups);
        setError('');
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      // Fallback to mock data on error
      console.log('Using fallback mock groups data due to error');
      const mockGroups = [
        {
          id: '1',
          name: 'Business',
          description: 'Business contacts',
          color: '#3B82F6',
          isDefault: true,
          cardCount: 0
        },
        {
          id: '2', 
          name: 'Personal',
          description: 'Personal contacts',
          color: '#10B981',
          isDefault: false,
          cardCount: 0
        },
        {
          id: '3',
          name: 'Clients',
          description: 'Client contacts',
          color: '#F59E0B',
          isDefault: false,
          cardCount: 0
        }
      ];
      setGroups(mockGroups);
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      icon: 'folder'
    });
    setEditDialog({ open: true, group: null });
  };

  const handleEditGroup = (group) => {
    setFormData({
      name: group.name,
      description: group.description || '',
      color: group.color,
      icon: group.icon
    });
    setEditDialog({ open: true, group });
  };

  const handleSaveGroup = async () => {
    if (!formData.name.trim()) {
      setError('Group name is required');
      return;
    }

    setLoading(true);
    try {
      const method = editDialog.group ? 'PUT' : 'POST';
      const url = editDialog.group ? `/groups/${editDialog.group.id}` : '/groups';
      
      const response = await makeApiCall(url, method, formData);
      
      if (response.success) {
        setEditDialog({ open: false, group: null });
        fetchGroups();
        setError('');
      } else {
        // Mock save for now
        console.log('Mocking group save:', formData);
        if (editDialog.group) {
          // Update existing group
          setGroups(prev => prev.map(g => g.id === editDialog.group.id ? { ...g, ...formData } : g));
        } else {
          // Add new group
          const newGroup = {
            id: Date.now().toString(),
            ...formData,
            isDefault: false,
            cardCount: 0
          };
          setGroups(prev => [...prev, newGroup]);
        }
        setEditDialog({ open: false, group: null });
        setError('');
      }
    } catch (err) {
      // Mock save for now on error
      console.log('Mocking group save due to error:', formData);
      if (editDialog.group) {
        // Update existing group
        setGroups(prev => prev.map(g => g.id === editDialog.group.id ? { ...g, ...formData } : g));
      } else {
        // Add new group
        const newGroup = {
          id: Date.now().toString(),
          ...formData,
          isDefault: false,
          cardCount: 0
        };
        setGroups(prev => [...prev, newGroup]);
      }
      setEditDialog({ open: false, group: null });
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    if (group.isDefault) {
      setError('Cannot delete default group');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${group.name}"? All cards in this group will be moved to ungrouped.`)) {
      setLoading(true);
      try {
        const response = await makeApiCall(`/groups/${group.id}`, 'DELETE');
        
        if (response.success) {
          fetchGroups();
          setError('');
        } else {
          // Mock delete for now
          console.log('Mocking group delete:', group.id);
          setGroups(prev => prev.filter(g => g.id !== group.id));
          setError('');
        }
      } catch (err) {
        // Mock delete for now on error
        console.log('Mocking group delete due to error:', group.id);
        setGroups(prev => prev.filter(g => g.id !== group.id));
        setError('');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleGroupClick = (group) => {
    onGroupSelect(group);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Manage Groups
          <Fab
            size="small"
            color="primary"
            onClick={handleCreateGroup}
            sx={{ ml: 2 }}
          >
            <AddIcon />
          </Fab>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={2}>
            {Array.isArray(groups) && groups.map((group) => (
              <Grid item xs={12} sm={6} md={4} key={group.id}>
                <Paper
                  elevation={selectedGroup?.id === group.id ? 3 : 1}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: selectedGroup?.id === group.id ? 2 : 0,
                    borderColor: 'primary.main',
                    '&:hover': {
                      elevation: 3,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                  onClick={() => handleGroupClick(group)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: group.color,
                        width: 32,
                        height: 32,
                        mr: 1,
                      }}
                    >
                      <FolderIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      {group.name}
                      {group.isDefault && (
                        <Chip
                          label="Default"
                          size="small"
                          sx={{ ml: 1 }}
                          color="primary"
                        />
                      )}
                    </Typography>
                  </Box>
                  
                  {group.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {group.description}
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {group.cardCount || 0} cards
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditGroup(group);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      
                      {!group.isDefault && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGroup(group);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
          
          {Array.isArray(groups) && groups.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <FolderOpenIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No groups yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first group to organize your business cards
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Create Group Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, group: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editDialog.group ? 'Edit Group' : 'Create New Group'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Group Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
            />
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {colorOptions.map((color) => (
                <Box
                  key={color}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    border: formData.color === color ? '3px solid #000' : '2px solid #fff',
                    boxShadow: 1,
                  }}
                  onClick={() => setFormData({ ...formData, color })}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, group: null })}>
            Cancel
          </Button>
          <Button onClick={handleSaveGroup} variant="contained" disabled={loading}>
            {editDialog.group ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GroupsManager;